import { useState, useEffect } from "react";
import {
  Label,
  Switch,
  Input,
  Button,
  HexColorPicker,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronRight,
  Settings,
  Plug,
  MessageCircle,
  Eye,
  EyeOff,
  Palette,
  X,
  PanelLeft,
  Type,
} from "lucide-react";
import {
  FONT_FAMILY_OPTIONS,
  type PortalSettings,
  type OpenLoopChatIntegration,
  type Theming,
  type Layout,
} from "../../../shared/schema";
import { preloadCuratedFonts, parseFontFamilyFromUrl } from "../../../shared/hooks/useFontLoader";
import {
  useOpenLoopChatPreviewEnabled,
  useSetOpenLoopChatPreviewEnabled,
} from "../../../shared/store/portalSchemaStore";

interface PortalSettingsEditorProps {
  settings: PortalSettings;
  onUpdateSettings: (updates: Partial<PortalSettings>) => void;
}

export const PortalSettingsEditor: React.FC<PortalSettingsEditorProps> = ({ settings, onUpdateSettings }) => {
  const { t } = useTranslation("portal");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLayoutExpanded, setIsLayoutExpanded] = useState(false);
  const [isThemingExpanded, setIsThemingExpanded] = useState(false);
  const [isIntegrationsExpanded, setIsIntegrationsExpanded] = useState(false);
  const [isOpenLoopChatExpanded, setIsOpenLoopChatExpanded] = useState(false);

  const isPreviewEnabled = useOpenLoopChatPreviewEnabled();
  const setPreviewEnabled = useSetOpenLoopChatPreviewEnabled();

  const openLoopChat = settings.integrations?.openLoopChat ?? { enabled: false };
  const theming = settings.theming ?? {};
  const layout = settings.layout ?? {};

  const [primaryColorInput, setPrimaryColorInput] = useState(theming.primaryColor || "#171717");
  const [secondaryColorInput, setSecondaryColorInput] = useState(theming.secondaryColor || "#f5f5f5");

  const handleOpenLoopChatUpdate = (updates: Partial<OpenLoopChatIntegration>) => {
    onUpdateSettings({
      integrations: {
        ...settings.integrations,
        openLoopChat: {
          ...openLoopChat,
          ...updates,
        },
      },
    });
  };

  const handleThemingUpdate = (updates: Partial<Theming>) => {
    if (updates.primaryColor !== undefined) {
      setPrimaryColorInput(updates.primaryColor || "#171717");
    }
    if (updates.secondaryColor !== undefined) {
      setSecondaryColorInput(updates.secondaryColor || "#f5f5f5");
    }
    onUpdateSettings({
      theming: {
        ...theming,
        ...updates,
      },
    });
  };

  const handleLayoutUpdate = (updates: Partial<Layout>) => {
    onUpdateSettings({
      layout: {
        ...layout,
        ...updates,
      },
    });
  };

  // Preload curated fonts when theming section is expanded for dropdown preview
  useEffect(() => {
    if (isThemingExpanded) {
      preloadCuratedFonts();
    }
  }, [isThemingExpanded]);

  // Determine if using a custom font (not in curated list)
  const isCustomFont = theming.fontFamily && !FONT_FAMILY_OPTIONS.some((opt) => opt.value === theming.fontFamily);
  const selectedFontValue = isCustomFont ? "custom" : theming.fontFamily || "system";

  return (
    <div className="space-y-2">
      {/* Collapsible header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <Settings className="h-4 w-4 mr-1" />
          {t("Portal Settings")}
        </button>
      </div>

      {/* Settings content (when expanded) */}
      {isExpanded && (
        <div className="space-y-4 pl-5">
          {/* Login Description */}
          <div className="space-y-1">
            <Label htmlFor="loginDescription" className="text-sm font-medium">
              {t("Login Description")}
            </Label>
            <Input
              id="loginDescription"
              value={settings.loginDescription ?? ""}
              onChange={(e) => onUpdateSettings({ loginDescription: e.target.value || undefined })}
              placeholder={t("Enter your email or phone number")}
              className="text-sm"
              maxLength={200}
            />
            <p className="text-xs text-muted-foreground">{t("Shown below the Sign In title on the login page")}</p>
          </div>

          {/* Login Input Label */}
          <div className="space-y-1">
            <Label htmlFor="loginInputLabel" className="text-sm font-medium">
              {t("Login Input Label")}
            </Label>
            <Input
              id="loginInputLabel"
              value={settings.loginInputLabel ?? ""}
              onChange={(e) => onUpdateSettings({ loginInputLabel: e.target.value || undefined })}
              placeholder={t("Email or Phone")}
              className="text-sm"
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground">{t("Label for the email/phone input on the login page")}</p>
          </div>

          {/* Disable Landing Page */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="disableLandingPage" className="text-sm font-medium">
                {t("Disable Landing Page")}
              </Label>
              <p className="text-xs text-muted-foreground">{t("Unauthenticated users will be redirected to login")}</p>
            </div>
            <Switch
              id="disableLandingPage"
              checked={settings.disableLandingPage ?? false}
              onCheckedChange={(checked) => onUpdateSettings({ disableLandingPage: checked })}
            />
          </div>

          {/* Disable Registration */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="disableRegistration" className="text-sm font-medium">
                {t("Disable Registration")}
              </Label>
              <p className="text-xs text-muted-foreground">{t("Registration page will redirect to login")}</p>
            </div>
            <Switch
              id="disableRegistration"
              checked={settings.disableRegistration ?? false}
              onCheckedChange={(checked) => onUpdateSettings({ disableRegistration: checked })}
            />
          </div>

          {/* Disable SMS for MFA */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="disableSMSForMFA" className="text-sm font-medium">
                {t("Disable SMS for MFA")}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("Only allow email for multi-factor authentication codes")}
              </p>
            </div>
            <Switch
              id="disableSMSForMFA"
              checked={settings.disableSMSForMFA ?? false}
              onCheckedChange={(checked) => onUpdateSettings({ disableSMSForMFA: checked })}
            />
          </div>

          {/* Layout Section */}
          <div className="space-y-2 pt-2">
            <button
              type="button"
              className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setIsLayoutExpanded(!isLayoutExpanded)}
            >
              {isLayoutExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <PanelLeft className="h-4 w-4 mr-1" />
              {t("Layout")}
            </button>

            {isLayoutExpanded && (
              <div className="space-y-4 pl-5">
                <p className="text-xs text-muted-foreground">{t("Configure how the portal navigation is displayed")}</p>

                {/* Enable Sidebar Navigation Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="enableSidebarNavigation" className="text-sm font-medium">
                      {t("Enable Sidebar Navigation")}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t("Show a sidebar navigation on larger screens (desktop)")}
                    </p>
                  </div>
                  <Switch
                    id="enableSidebarNavigation"
                    checked={layout.enableSidebarNavigation ?? false}
                    onCheckedChange={(checked) => handleLayoutUpdate({ enableSidebarNavigation: checked })}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Theming Section */}
          <div className="space-y-2 pt-2">
            <button
              type="button"
              className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setIsThemingExpanded(!isThemingExpanded)}
            >
              {isThemingExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <Palette className="h-4 w-4 mr-1" />
              {t("Theming")}
            </button>

            {isThemingExpanded && (
              <div className="space-y-4 pl-5">
                <p className="text-xs text-muted-foreground">{t("Customize the portal colors to match your brand")}</p>

                {/* Primary Color */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t("Primary Color")}</Label>
                  <div className="flex items-center gap-3">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="h-10 w-10 rounded-md border border-input shadow-sm cursor-pointer hover:ring-2 hover:ring-ring hover:ring-offset-2 transition-all"
                          style={{ backgroundColor: theming.primaryColor || "#171717" }}
                          aria-label={t("Choose primary color")}
                        />
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-3" align="start">
                        <HexColorPicker
                          color={theming.primaryColor || "#171717"}
                          onChange={(color) => handleThemingUpdate({ primaryColor: color })}
                        />
                      </PopoverContent>
                    </Popover>
                    <Input
                      value={primaryColorInput}
                      onChange={(e) => setPrimaryColorInput(e.target.value)}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value.match(/^#[0-9A-Fa-f]{6}$/)) {
                          handleThemingUpdate({ primaryColor: value });
                        } else {
                          setPrimaryColorInput(theming.primaryColor || "#171717");
                        }
                      }}
                      placeholder="#171717"
                      className="w-28 font-mono text-sm h-10"
                    />
                    {theming.primaryColor && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleThemingUpdate({ primaryColor: undefined })}
                        className="h-8 w-8 p-0"
                        aria-label={t("Reset primary color")}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{t("Used for buttons, links, and primary actions")}</p>
                </div>

                {/* Secondary Color */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t("Secondary Color")}</Label>
                  <div className="flex items-center gap-3">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="h-10 w-10 rounded-md border border-input shadow-sm cursor-pointer hover:ring-2 hover:ring-ring hover:ring-offset-2 transition-all"
                          style={{ backgroundColor: theming.secondaryColor || "#f5f5f5" }}
                          aria-label={t("Choose secondary color")}
                        />
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-3" align="start">
                        <HexColorPicker
                          color={theming.secondaryColor || "#f5f5f5"}
                          onChange={(color) => handleThemingUpdate({ secondaryColor: color })}
                        />
                      </PopoverContent>
                    </Popover>
                    <Input
                      value={secondaryColorInput}
                      onChange={(e) => setSecondaryColorInput(e.target.value)}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value.match(/^#[0-9A-Fa-f]{6}$/)) {
                          handleThemingUpdate({ secondaryColor: value });
                        } else {
                          setSecondaryColorInput(theming.secondaryColor || "#f5f5f5");
                        }
                      }}
                      placeholder="#f5f5f5"
                      className="w-28 font-mono text-sm h-10"
                    />
                    {theming.secondaryColor && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleThemingUpdate({ secondaryColor: undefined })}
                        className="h-8 w-8 p-0"
                        aria-label={t("Reset secondary color")}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{t("Used for secondary buttons and backgrounds")}</p>
                </div>

                {/* Font Family */}
                <div className="space-y-2 pt-2 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Type className="h-4 w-4 text-muted-foreground" />
                    <Label className="text-sm font-medium">{t("Font Family")}</Label>
                  </div>
                  <Select
                    value={selectedFontValue}
                    onValueChange={(value) => {
                      if (value === "system") {
                        handleThemingUpdate({ fontFamily: undefined, customFontUrl: undefined });
                      } else if (value === "custom") {
                        handleThemingUpdate({ fontFamily: "custom" });
                      } else {
                        handleThemingUpdate({ fontFamily: value, customFontUrl: undefined });
                      }
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t("Select a font")} />
                    </SelectTrigger>
                    <SelectContent>
                      {FONT_FAMILY_OPTIONS.map((font) => (
                        <SelectItem
                          key={font.value}
                          value={font.value}
                          style={{
                            fontFamily: font.value === "system" ? undefined : `"${font.value}", sans-serif`,
                          }}
                        >
                          {font.label}
                        </SelectItem>
                      ))}
                      <SelectItem value="custom">{t("Custom")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t("Choose a font from the list or use a custom Google Fonts URL")}
                  </p>

                  {/* Custom Font URL Input */}
                  {(selectedFontValue === "custom" || theming.customFontUrl) && (
                    <div className="space-y-2 pt-2">
                      <Label className="text-sm font-medium">{t("Custom Font URL")}</Label>
                      <Input
                        value={theming.customFontUrl || ""}
                        onChange={(e) => {
                          const url = e.target.value || undefined;
                          const parsedFontFamily = url ? parseFontFamilyFromUrl(url) : undefined;
                          handleThemingUpdate({
                            customFontUrl: url,
                            // Auto-populate font family name if successfully parsed
                            ...(parsedFontFamily && { fontFamily: parsedFontFamily }),
                          });
                        }}
                        placeholder="https://fonts.googleapis.com/css2?family=..."
                        className="text-sm"
                      />
                      <p className="text-xs text-muted-foreground">
                        {t("Paste a Google Fonts CSS URL for custom fonts")}
                      </p>
                      {theming.customFontUrl && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">{t("Font Family Name")}</Label>
                          <Input
                            value={theming.fontFamily === "custom" ? "" : theming.fontFamily || ""}
                            onChange={(e) => handleThemingUpdate({ fontFamily: e.target.value || undefined })}
                            placeholder="e.g., Bebas Neue"
                            className="text-sm"
                          />
                          <p className="text-xs text-muted-foreground">
                            {t("Enter the exact font family name from your Google Fonts URL")}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Integrations Section */}
          <div className="space-y-2 pt-2">
            <button
              type="button"
              className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setIsIntegrationsExpanded(!isIntegrationsExpanded)}
            >
              {isIntegrationsExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <Plug className="h-4 w-4 mr-1" />
              {t("Integrations")}
            </button>

            {isIntegrationsExpanded && (
              <div className="space-y-3 pl-5">
                {/* OpenLoop Chat Integration */}
                <div className="space-y-2">
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                    onClick={() => setIsOpenLoopChatExpanded(!isOpenLoopChatExpanded)}
                  >
                    {isOpenLoopChatExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <MessageCircle className="h-4 w-4 mr-1" />
                    {t("OpenLoop Chat")}
                  </button>

                  {isOpenLoopChatExpanded && (
                    <div className="space-y-4 pl-5">
                      {/* Enable Toggle */}
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label htmlFor="openLoopChatEnabled" className="text-sm font-medium">
                            {t("Enable OpenLoop Chat")}
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            {t("Show Amazon Connect chat widget on all portal pages")}
                          </p>
                        </div>
                        <Switch
                          id="openLoopChatEnabled"
                          checked={openLoopChat.enabled ?? false}
                          onCheckedChange={(checked) => handleOpenLoopChatUpdate({ enabled: checked })}
                        />
                      </div>

                      {/* Preview Toggle (only shown when enabled) */}
                      {openLoopChat.enabled && (
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant={isPreviewEnabled ? "default" : "outline"}
                            size="sm"
                            onClick={() => setPreviewEnabled(!isPreviewEnabled)}
                            className="gap-2"
                          >
                            {isPreviewEnabled ? (
                              <>
                                <EyeOff className="h-4 w-4" />
                                {t("Hide Preview")}
                              </>
                            ) : (
                              <>
                                <Eye className="h-4 w-4" />
                                {t("Preview Widget")}
                              </>
                            )}
                          </Button>
                          {isPreviewEnabled && (
                            <span className="text-xs text-muted-foreground">{t("Widget is visible on this page")}</span>
                          )}
                        </div>
                      )}

                      {/* Configuration fields (only shown when enabled) */}
                      {openLoopChat.enabled && (
                        <div className="space-y-4">
                          {/* Source URL */}
                          <div className="space-y-1">
                            <Label htmlFor="openLoopChatSource" className="text-sm font-medium">
                              {t("Amazon Connect Script URL")}
                            </Label>
                            <Input
                              id="openLoopChatSource"
                              value={openLoopChat.source ?? ""}
                              onChange={(e) => handleOpenLoopChatUpdate({ source: e.target.value })}
                              placeholder="https://example.my.connect.aws/.../amazon-connect-chat-interface-client.js"
                              className="text-sm"
                            />
                            <p className="text-xs text-muted-foreground">
                              {t("The URL to the Amazon Connect chat widget script")}
                            </p>
                          </div>

                          {/* Snippet ID */}
                          <div className="space-y-1">
                            <Label htmlFor="openLoopChatSnippetId" className="text-sm font-medium">
                              {t("Snippet ID")}
                            </Label>
                            <Input
                              id="openLoopChatSnippetId"
                              value={openLoopChat.snippetId ?? ""}
                              onChange={(e) => handleOpenLoopChatUpdate({ snippetId: e.target.value })}
                              placeholder="QVFJREFIaFg3VVJ2dFRXb2IrUUdndUkvUzRJ..."
                              className="text-sm font-mono"
                            />
                            <p className="text-xs text-muted-foreground">
                              {t("Base64 encoded snippet ID from Amazon Connect")}
                            </p>
                          </div>

                          {/* Session ID */}
                          <div className="space-y-1">
                            <Label htmlFor="openLoopChatSessionId" className="text-sm font-medium">
                              {t("Session ID")}
                            </Label>
                            <Input
                              id="openLoopChatSessionId"
                              value={openLoopChat.sessionId ?? ""}
                              onChange={(e) => handleOpenLoopChatUpdate({ sessionId: e.target.value })}
                              placeholder="c16e02ca-e4c7-4a5c-99ea-07b7daf80b4b"
                              className="text-sm font-mono"
                            />
                            <p className="text-xs text-muted-foreground">
                              {t("Unique session identifier for the chat widget")}
                            </p>
                          </div>

                          {/* Platform Name */}
                          <div className="space-y-1">
                            <Label htmlFor="openLoopChatPlatformName" className="text-sm font-medium">
                              {t("Platform Name")}
                            </Label>
                            <Input
                              id="openLoopChatPlatformName"
                              value={openLoopChat.platformName ?? ""}
                              onChange={(e) => handleOpenLoopChatUpdate({ platformName: e.target.value })}
                              placeholder="Your Platform Name"
                              className="text-sm"
                            />
                            <p className="text-xs text-muted-foreground">
                              {t("Platform name for chat routing (must be pre-configured in Amazon Connect)")}
                            </p>
                          </div>

                          {/* Branding Section */}
                          <div className="space-y-4 pt-4 border-t border-border">
                            <p className="text-sm font-medium text-muted-foreground">{t("Branding")}</p>

                            {/* Header Message */}
                            <div className="space-y-1">
                              <Label htmlFor="openLoopChatHeaderMessage" className="text-sm font-medium">
                                {t("Header Message")}
                              </Label>
                              <Input
                                id="openLoopChatHeaderMessage"
                                value={openLoopChat.headerMessage ?? ""}
                                onChange={(e) => handleOpenLoopChatUpdate({ headerMessage: e.target.value })}
                                placeholder="Welcome!"
                                className="text-sm"
                                maxLength={50}
                              />
                              <p className="text-xs text-muted-foreground">
                                {t("Text displayed in the chat widget header (under the logo)")}
                              </p>
                            </div>

                            {/* Logo URL */}
                            <div className="space-y-1">
                              <Label htmlFor="openLoopChatLogoUrl" className="text-sm font-medium">
                                {t("Logo URL")}
                              </Label>
                              <Input
                                id="openLoopChatLogoUrl"
                                value={openLoopChat.logoUrl ?? ""}
                                onChange={(e) => handleOpenLoopChatUpdate({ logoUrl: e.target.value })}
                                placeholder="https://example.com/logo.png"
                                className="text-sm"
                              />
                              <p className="text-xs text-muted-foreground">
                                {t("URL to a .png, .jpg, or .svg image for the chat widget header")}
                              </p>
                            </div>

                            {/* Logo Alt Text */}
                            <div className="space-y-1">
                              <Label htmlFor="openLoopChatLogoAltText" className="text-sm font-medium">
                                {t("Logo Alt Text")}
                              </Label>
                              <Input
                                id="openLoopChatLogoAltText"
                                value={openLoopChat.logoAltText ?? ""}
                                onChange={(e) => handleOpenLoopChatUpdate({ logoAltText: e.target.value })}
                                placeholder="Company Logo"
                                className="text-sm"
                              />
                              <p className="text-xs text-muted-foreground">
                                {t("Alternative text for the logo image (for accessibility)")}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
